import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Collapse,
  Tooltip,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import {
  AddShoppingCart as AddCartIcon,
  History as HistoryIcon,
  LocalPharmacy as PharmacyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  LocalShipping as DeliveryIcon,
  Store as StoreIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 0,
  },
  orderCard: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  orderHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
  orderHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  orderNumber: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  orderDate: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  orderHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  orderTotal: {
    fontWeight: 600,
    fontSize: "1rem",
    color: theme.palette.success.dark,
  },
  orderItemCount: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  orderItems: {
    padding: theme.spacing(0),
  },
  tableContainer: {
    maxHeight: 300,
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    "& th": {
      fontWeight: 600,
      fontSize: "0.75rem",
    },
  },
  productCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  productImage: {
    width: 36,
    height: 36,
    borderRadius: 4,
    objectFit: "cover",
    backgroundColor: theme.palette.grey[200],
  },
  productImagePlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[400],
  },
  productInfo: {
    display: "flex",
    flexDirection: "column",
  },
  productName: {
    fontWeight: 500,
    fontSize: "0.8rem",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  productBarcode: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },
  categoryChip: {
    fontSize: "0.65rem",
    height: 18,
    marginTop: 2,
  },
  addButton: {
    color: theme.palette.primary.main,
    padding: 4,
    "&:hover": {
      backgroundColor: theme.palette.primary.light + "20",
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
  },
  quantityCell: {
    textAlign: "center",
  },
  priceCell: {
    textAlign: "right",
  },
  deliveryChip: {
    fontSize: "0.7rem",
    height: 22,
  },
  statusChip: {
    fontSize: "0.7rem",
    height: 22,
  },
  expandIcon: {
    transition: "transform 0.2s",
  },
  summaryInfo: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const OrderCard = ({ order, onAddToCart, classes }) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleAddToCart = (item) => {
    if (onAddToCart) {
      onAddToCart({
        id: item.productId,
        name: item.productName,
        barcode: item.productBarcode,
        imageUrl: item.productImageUrl,
        price: item.unitPrice,
      });
    }
  };

  // Calculate order total from items
  const orderTotal = order.items.reduce((sum, item) => sum + (item.total || 0), 0);

  return (
    <Paper className={classes.orderCard} elevation={0}>
      <Box className={classes.orderHeader} onClick={handleToggle}>
        <Box className={classes.orderHeaderLeft}>
          <ReceiptIcon color="primary" fontSize="small" />
          <Box>
            <Typography className={classes.orderNumber}>
              Pedido #{order.orderNumber}
            </Typography>
            <Typography className={classes.orderDate}>
              {formatDateTime(order.orderDate)}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.orderHeaderRight}>
          <Box textAlign="right">
            <Typography className={classes.orderTotal}>
              {formatCurrency(orderTotal)}
            </Typography>
            <Typography className={classes.orderItemCount}>
              {order.items.length} {order.items.length === 1 ? "item" : "itens"}
            </Typography>
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box className={classes.orderItems}>
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell className={classes.quantityCell}>Qtd</TableCell>
                  <TableCell className={classes.priceCell}>Preço</TableCell>
                  <TableCell className={classes.priceCell}>Total</TableCell>
                  <TableCell align="center" style={{ width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items.map((item, index) => (
                  <TableRow key={`${item.productId}-${index}`} hover>
                    <TableCell>
                      <Box className={classes.productCell}>
                        {item.productImageUrl ? (
                          <img
                            src={item.productImageUrl}
                            alt={item.productName}
                            className={classes.productImage}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <Box className={classes.productImagePlaceholder}>
                            <PharmacyIcon fontSize="small" />
                          </Box>
                        )}
                        <Box className={classes.productInfo}>
                          <Tooltip title={item.productName}>
                            <Typography className={classes.productName}>
                              {item.productName}
                            </Typography>
                          </Tooltip>
                          {item.productBarcode && (
                            <Typography className={classes.productBarcode}>
                              {item.productBarcode}
                            </Typography>
                          )}
                          {item.categoryName && (
                            <Chip
                              size="small"
                              label={item.categoryName}
                              className={classes.categoryChip}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.quantityCell}>
                      {item.quantity}
                    </TableCell>
                    <TableCell className={classes.priceCell}>
                      {formatCurrency(item.unitPrice)}
                    </TableCell>
                    <TableCell className={classes.priceCell}>
                      {formatCurrency(item.total)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Adicionar ao carrinho">
                        <IconButton
                          size="small"
                          className={classes.addButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(item);
                          }}
                        >
                          <AddCartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Paper>
  );
};

const PurchaseHistoryTab = ({
  purchaseHistory,
  loading,
  page,
  onPageChange,
  onAddToCart,
}) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!purchaseHistory || !purchaseHistory.items || purchaseHistory.items.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <HistoryIcon style={{ fontSize: 48, opacity: 0.5 }} />
        <Typography variant="body1">Nenhum histórico de compras</Typography>
        <Typography variant="body2" color="textSecondary">
          Os pedidos deste cliente aparecerão aqui
        </Typography>
      </Box>
    );
  }

  const handlePageChange = (event, newPage) => {
    onPageChange(newPage);
  };

  // Group items by order
  const ordersMap = purchaseHistory.items.reduce((acc, item) => {
    if (!acc[item.orderId]) {
      acc[item.orderId] = {
        orderId: item.orderId,
        orderNumber: item.orderNumber,
        orderDate: item.orderDate,
        items: [],
      };
    }
    acc[item.orderId].items.push(item);
    return acc;
  }, {});

  // Convert to array and sort by date (most recent first)
  const orders = Object.values(ordersMap).sort(
    (a, b) => new Date(b.orderDate) - new Date(a.orderDate)
  );

  return (
    <Box className={classes.container}>
      {orders.map((order) => (
        <OrderCard
          key={order.orderId}
          order={order}
          onAddToCart={onAddToCart}
          classes={classes}
        />
      ))}

      {purchaseHistory.totalPages > 1 && (
        <Box className={classes.paginationContainer}>
          <Pagination
            count={purchaseHistory.totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="small"
          />
        </Box>
      )}

      {/* Summary */}
      <Box className={classes.summaryInfo}>
        <Typography variant="body2" color="textSecondary">
          Exibindo {orders.length} pedidos com {purchaseHistory.items.length} itens
          {purchaseHistory.total > purchaseHistory.items.length && (
            <> (de {purchaseHistory.total} itens no total)</>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default PurchaseHistoryTab;
